import React from "react";

import Select from "antd/es/select";
import cn from "classnames";
import { capitalize } from "lodash-es";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { LegacySimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";

import EntityBadge from "@app/component/EntityBadge";

import styles from "./ColorFilter.module.scss";
import getHumanReadableDescriptionForOptionsFilter, {
  OptionFilterOperator,
} from "./getHumanReadableDescriptionForOptionsFilter";

export const COLOR_FILTER_OPERATORS = [FilterOperator.IN_ANY, FilterOperator.NONE_OF];

const doesSupportValue = (value: any, operator: FilterOperator) =>
  Array.isArray(value) &&
  value.every((item) => typeof item === "string") &&
  COLOR_FILTER_OPERATORS.includes(operator);

interface ColorFilterProps extends IFilterComponentProps {}

const ColorFilter: IFilterInstance = {
  doesSupportOption: (option: FilterOption) => option === FilterOption.AVAILABLE_COLORS,
  doesSupportValue,
  getComponent:
    (): ((props: ColorFilterProps) => JSX.Element) =>
    ({ className, disabled, onChange, options, placeholder, value }: ColorFilterProps) => {
      const intl = useIntl();

      const colors = (options?.availableColors ?? []) as Color[];

      return (
        <Select<Color[]>
          className={cn(styles.container, className)}
          disabled={disabled}
          filterOption={defaultFilterOption}
          mode="multiple"
          onChange={(colors: Color[]) => onChange?.({ ...value, value: colors })}
          placeholder={
            placeholder ??
            intl.formatMessage({
              id: "filters.color.select.placeholder",
              defaultMessage: "Click or type to select colors",
              description: "Placeholder displayed in ColorFilter's select field",
            })
          }
          value={Array.isArray(value.value) ? value.value : []}
        >
          {colors.map((color) => (
            <Select.Option key={color} label={color} value={color}>
              <EntityBadge entity={{ color, name: capitalize(color) }} />
            </Select.Option>
          ))}
        </Select>
      );
    },
  getHumanReadableDescription: (value: LegacySimpleCondition, field: IField) =>
    doesSupportValue(value.value, value.operator)
      ? getHumanReadableDescriptionForOptionsFilter(
          field,
          value.operator as OptionFilterOperator,
          value.value.map(capitalize)
        )
      : undefined,
};

export default ColorFilter;
